import {
  cloneElement,
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.scss';

interface TooltipProps {
  children: ReactElement<{ 'aria-describedby'?: string }>;
  content?: ReactNode;
}

interface TooltipPosition {
  left: number;
  top: number;
}

const VIEWPORT_MARGIN = 8;
const TRIGGER_GAP = 12;

const Tooltip: FC<TooltipProps> = ({ children, content }) => {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<TooltipPosition>();

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip) return;

    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const fitsRight =
      triggerRect.right + TRIGGER_GAP + tooltipRect.width <= window.innerWidth - VIEWPORT_MARGIN;
    const fitsLeft = triggerRect.left - TRIGGER_GAP - tooltipRect.width >= VIEWPORT_MARGIN;
    const left =
      fitsRight || !fitsLeft
        ? Math.min(
            triggerRect.right + TRIGGER_GAP,
            window.innerWidth - tooltipRect.width - VIEWPORT_MARGIN,
          )
        : triggerRect.left - tooltipRect.width - TRIGGER_GAP;
    const top = Math.min(
      Math.max(
        triggerRect.top + (triggerRect.height - tooltipRect.height) / 2,
        VIEWPORT_MARGIN,
      ),
      window.innerHeight - tooltipRect.height - VIEWPORT_MARGIN,
    );

    setPosition({ left: Math.max(left, VIEWPORT_MARGIN), top });
  }, []);

  useLayoutEffect(() => {
    if (!isVisible) return;

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isVisible, updatePosition]);

  const describedBy = [children.props['aria-describedby'], content ? tooltipId : undefined]
    .filter(Boolean)
    .join(' ');
  const trigger = content
    ? cloneElement(children, { 'aria-describedby': describedBy })
    : children;

  return (
    <>
      <span
        ref={triggerRef}
        className={styles.trigger}
        onMouseEnter={() => content && setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onFocus={() => content && setIsVisible(true)}
        onBlur={() => setIsVisible(false)}
      >
        {trigger}
      </span>
      {content &&
        isVisible &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            className={styles.tooltip}
            style={{
              left: position?.left ?? 0,
              top: position?.top ?? 0,
              visibility: position ? 'visible' : 'hidden',
            }}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
};

export default Tooltip;
